import { useState, useEffect, useMemo } from 'react';

/**
 * Tick a `now` timestamp every second while `stopped` is false.
 * Returns the elapsed milliseconds between `startMs` and either
 * `endMs` (if provided) or the current time.
 *
 * Returns `null` when `startMs` is null/undefined, which signals
 * "nothing to measure yet."
 */
export function useElapsedTime(
  startMs: number | null | undefined,
  options?: { endMs?: number | null; stopped?: boolean },
): number | null {
  const endMs = options?.endMs ?? null;
  const stopped = options?.stopped ?? false;

  const [now, setNow] = useState(() => Date.now());

  const shouldTick = startMs != null && endMs == null && !stopped;

  useEffect(() => {
    if (!shouldTick) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [shouldTick]);

  return useMemo(() => {
    if (startMs == null) return null;
    const end = endMs ?? now;
    const diff = end - startMs;
    return Math.max(0, diff);
  }, [startMs, endMs, now]);
}
